'use client';

import React, { useState, useEffect } from 'react';
import { ArrowTopRightOnSquareIcon, MegaphoneIcon, XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { createClient } from '@/lib/local-api/client';
import Link from 'next/link';

interface MiniAd {
  id: string;
  project_name: string;
  project_url: string | null;
  headline: string;
  cta_text: string | null;
  image_url: string | null;
  project_category: string;
}

interface MiniAdCardProps {
  placementSlot: string;
  variant?: 'pill' | 'card';
  className?: string;
}

const SAMPLE_PILLS = [
  { id: 'sp-1', headline: 'This pill slot is available — advertise your project here', project_name: 'Your Brand', project_url: null, cta_text: 'Book Slot', image_url: null, project_category: 'Sponsored' },
  { id: 'sp-2', headline: 'Reach crypto investors with a compact pill ad', project_name: 'Your Project', project_url: null, cta_text: 'Place Ad', image_url: null, project_category: 'Crypto / DeFi' },
];

const SAMPLE_CARDS = [
  { id: 'sc-1', headline: 'Your Ad Here — Small Card Slot', project_name: 'Your Brand', project_url: null, cta_text: 'Advertise', image_url: null, project_category: 'Sponsored' },
  { id: 'sc-2', headline: 'Promote Your Project in This Card', project_name: 'Your Project', project_url: null, cta_text: 'Book Now', image_url: null, project_category: 'Crypto / Forex' },
];

export default function MiniAdCard({ placementSlot, variant = 'pill', className = '' }: MiniAdCardProps) {
  const [ads, setAds] = useState<MiniAd[]>([]);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const sampleAds = variant === 'pill' ? SAMPLE_PILLS : SAMPLE_CARDS;
  const displayAds = ads.length > 0 ? ads : sampleAds;
  const isSampleMode = loaded && ads.length === 0;

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('ad_placements')
      .select('id, project_name, project_url, headline, cta_text, image_url, project_category')
      .eq('ad_status', 'approved')
      .eq('placement_slot', placementSlot)
      .order('created_at', { ascending: false })
      .limit(4)
      .then(({ data }) => {
        if (data && data.length > 0) setAds(data as MiniAd[]);
        setLoaded(true);
      });
  }, [placementSlot]);

  useEffect(() => {
    if (displayAds.length <= 1) return;
    const t = setInterval(() => setIdx((p) => (p + 1) % displayAds.length), isSampleMode ? 4000 : 8000);
    return () => clearInterval(t);
  }, [displayAds.length, isSampleMode]);

  if (!loaded || dismissed) return null;

  const ad = displayAds[idx];

  if (variant === 'pill') {
    return (
      <div className={`relative flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-500/8 to-primary/8 border ${isSampleMode ? 'border-dashed border-amber-500/30' : 'border-amber-500/15'} rounded-full text-xs overflow-hidden group ${className}`}>
        {isSampleMode && (
          <span className="text-[8px] font-bold uppercase tracking-widest text-primary bg-primary/10 px-1.5 py-0.5 rounded-full flex-shrink-0">Slot</span>
        )}
        {!isSampleMode && (
          <span className="text-[8px] font-bold uppercase tracking-widest text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded-full flex-shrink-0">Ad</span>
        )}
        {ad.image_url && (
          <img src={ad.image_url} alt={ad.project_name} className="w-4 h-4 rounded-full object-cover flex-shrink-0" />
        )}
        {isSampleMode && !ad.image_url && (
          <SparklesIcon style={{ width: 11, height: 11 }} className="text-primary/60 flex-shrink-0" />
        )}
        <span className="text-foreground font-medium truncate flex-1">{ad.headline}</span>
        <Link
          href={isSampleMode ? `/place-ad?slot=${placementSlot}` : (ad.project_url || '#')}
          className="flex items-center gap-0.5 text-amber-500 font-semibold hover:underline flex-shrink-0 whitespace-nowrap"
        >
          {ad.cta_text || 'Book'}
          <ArrowTopRightOnSquareIcon style={{ width: 9, height: 9 }} />
        </Link>
        {!isSampleMode && (
          <button
            onClick={() => setDismissed(true)}
            className="ml-1 p-0.5 rounded-full text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
          >
            <XMarkIcon style={{ width: 10, height: 10 }} />
          </button>
        )}
      </div>
    );
  }

  // card variant
  return (
    <div className={`relative bg-gradient-to-br from-amber-500/8 via-card to-primary/8 border ${isSampleMode ? 'border-dashed border-amber-500/30' : 'border-amber-500/20'} rounded-2xl overflow-hidden group hover:border-amber-500/40 hover:shadow-md transition-all ${className}`}>
      <span className={`absolute top-2 left-2 z-10 text-[8px] font-bold uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full ${isSampleMode ? 'bg-primary/10 text-primary/70' : 'bg-card/90 text-muted-foreground border border-border/60'}`}>
        {isSampleMode ? 'Ad Slot' : 'Sponsored'}
      </span>
      {!isSampleMode && (
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-2 right-2 z-10 p-1 rounded-full bg-card/90 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <XMarkIcon style={{ width: 10, height: 10 }} />
        </button>
      )}
      <div className="flex items-center gap-3 px-3 pb-3 pt-7 min-h-[104px]">
        {ad.image_url ? (
          <img src={ad.image_url} alt={ad.project_name} className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-xl object-cover flex-shrink-0 border border-border/60 bg-muted" />
        ) : (
          <div className={`w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-xl flex items-center justify-center flex-shrink-0 ${isSampleMode ? 'bg-primary/10' : 'bg-amber-500/15'}`}>
            {isSampleMode ? (
              <MegaphoneIcon style={{ width: 24, height: 24 }} className="text-primary/70" />
            ) : (
              <MegaphoneIcon style={{ width: 24, height: 24 }} className="text-amber-500" />
            )}
          </div>
        )}
        <div className="flex-1 min-w-0 self-stretch flex flex-col justify-center">
          <p className="text-base sm:text-lg font-extrabold text-foreground leading-tight truncate" title={ad.project_name}>{ad.project_name}</p>
          <p className="text-[11px] sm:text-xs text-muted-foreground leading-snug line-clamp-2 mt-1">{ad.headline}</p>
          <div className="flex items-center justify-between gap-2 mt-2">
            <span className="text-[9px] text-muted-foreground truncate">{ad.project_category}</span>
            <Link
              href={isSampleMode ? `/place-ad?slot=${placementSlot}` : (ad.project_url || '#')}
              className="flex items-center gap-1 text-[11px] font-bold text-amber-500 hover:underline flex-shrink-0"
            >
              {ad.cta_text || 'Book'}
              <ArrowTopRightOnSquareIcon style={{ width: 10, height: 10 }} />
            </Link>
          </div>
        </div>
      </div>
      {/* progress bar */}
      {displayAds.length > 1 && (
        <div className="flex gap-0.5 px-2.5 pb-1.5">
          {displayAds.map((_, i) => (
            <div key={i} className={`h-0.5 flex-1 rounded-full transition-all ${i === idx ? 'bg-amber-500' : 'bg-muted'}`} />
          ))}
        </div>
      )}
    </div>
  );
}
